import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Sprout, Flower2, Sparkles, Trophy, Zap, Target, Check, Lock, Star, TreePine } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STAGE_VISUALS = {
  seed: { scale: 'scale-50', opacity: 'opacity-40', animation: '' },
  sprout: { scale: 'scale-75', opacity: 'opacity-60', animation: 'animate-pulse' },
  bud: { scale: 'scale-90', opacity: 'opacity-80', animation: '' },
  bloom: { scale: 'scale-100', opacity: 'opacity-100', animation: '' },
  radiant: { scale: 'scale-110', opacity: 'opacity-100', animation: 'animate-glow' },
  wilted: { scale: 'scale-75', opacity: 'opacity-30', animation: '' },
};

const PlantIcon = ({ plant, size = 'w-10 h-10' }) => {
  const vis = STAGE_VISUALS[plant.stage] || STAGE_VISUALS.seed;
  const isRadiant = plant.stage === 'radiant';
  const isWilted = plant.stage === 'wilted';

  return (
    <div className={`relative ${vis.animation}`} title={`${plant.name} — ${plant.stage}`}>
      <div
        className={`${size} rounded-full flex items-center justify-center transition-all duration-500 ${vis.scale} ${vis.opacity} ${isWilted ? 'grayscale' : ''}`}
        style={{ backgroundColor: `${plant.color}20`, border: `2px solid ${plant.color}${isRadiant ? '' : '40'}` }}
      >
        {plant.stage === 'seed' && <Sprout className="w-1/2 h-1/2" style={{ color: plant.color }} />}
        {plant.stage === 'sprout' && <Sprout className="w-1/2 h-1/2" style={{ color: plant.color }} />}
        {plant.stage === 'bud' && <Flower2 className="w-1/2 h-1/2" style={{ color: plant.color }} />}
        {plant.stage === 'bloom' && <Flower2 className="w-1/2 h-1/2" style={{ color: plant.color }} />}
        {plant.stage === 'radiant' && <Sparkles className="w-1/2 h-1/2" style={{ color: plant.color }} />}
        {plant.stage === 'wilted' && <Sprout className="w-1/2 h-1/2 text-gray-400" />}
      </div>
      {isRadiant && (
        <div className="absolute -inset-1 rounded-full animate-ping" style={{ backgroundColor: `${plant.color}15` }} />
      )}
    </div>
  );
};

const GardenGrid = ({ plants }) => {
  if (!plants || plants.length === 0) {
    return (
      <div className="text-center py-8">
        <Sprout className="w-12 h-12 text-[#E5D0C9] mx-auto mb-3" />
        <p className="text-sm text-[#8C7A76]">Your garden is empty</p>
        <p className="text-xs text-[#8C7A76] mt-1">Log your first day to plant a seed!</p>
      </div>
    );
  }

  return (
    <div className="relative bg-gradient-to-b from-[#F0EDE6] to-[#E8E4D8] rounded-2xl p-6 min-h-[200px] overflow-hidden">
      {/* Ground decoration */}
      <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-[#C4B99A]/20 to-transparent" />
      {/* Butterflies for 7+ plants */}
      {plants.length >= 7 && (
        <>
          <div className="absolute top-4 right-8 text-[#4ADE80] animate-bounce" style={{ animationDuration: '3s' }}>
            <Sparkles className="w-4 h-4" />
          </div>
          <div className="absolute top-8 left-12 text-[#A388EE] animate-bounce" style={{ animationDuration: '4s', animationDelay: '1s' }}>
            <Sparkles className="w-3 h-3" />
          </div>
        </>
      )}
      <div className="flex flex-wrap gap-3 justify-center relative z-10">
        {plants.map((plant) => (
          <div key={plant.plant_id} className="group relative">
            <PlantIcon plant={plant} />
            <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-[#1A1514] text-white text-[10px] px-2 py-1 rounded whitespace-nowrap z-20">
              {plant.name} — Day {plant.days_alive}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const LevelProgress = ({ level, totalPlants, totalXp }) => {
  const next = level.next_level;
  const progress = next ? Math.min(100, ((totalPlants - level.seeds_needed) / (next.seeds_needed - level.seeds_needed)) * 100) : 100;

  return (
    <div className="flex items-center gap-4 p-4 bg-[#FFF2EC] rounded-xl">
      <div className="w-12 h-12 rounded-full bg-[#FF6B4A]/10 flex items-center justify-center flex-shrink-0">
        <TreePine className="w-6 h-6 text-[#FF6B4A]" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1">
          <p className="font-semibold text-sm text-[#1A1514]">Lv.{level.level} {level.name}</p>
          <div className="flex items-center gap-1 text-xs text-[#A388EE] font-semibold">
            <Zap className="w-3 h-3" /> {totalXp} XP
          </div>
        </div>
        <div className="w-full h-2 bg-[#E5D0C9] rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-[#4ADE80] to-[#FF6B4A] rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
        </div>
        {next && (
          <p className="text-[10px] text-[#8C7A76] mt-1">{totalPlants}/{next.seeds_needed} plants to Lv.{next.level} {next.name}</p>
        )}
      </div>
    </div>
  );
};

const ChallengeCard = ({ challenge }) => (
  <div className={`flex items-center gap-3 p-3 rounded-xl transition-colors ${challenge.completed ? 'bg-[#4ADE80]/10' : 'bg-[#FFF2EC]'}`} data-testid={`challenge-${challenge.id}`}>
    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${challenge.completed ? 'bg-[#4ADE80]' : 'bg-[#E5D0C9]'}`}>
      {challenge.completed ? <Check className="w-4 h-4 text-white" /> : <Target className="w-4 h-4 text-[#8C7A76]" />}
    </div>
    <div className="flex-1 min-w-0">
      <p className={`text-sm ${challenge.completed ? 'text-[#FF6B4A] font-semibold line-through' : 'text-[#1A1514]'}`}>{challenge.text}</p>
    </div>
    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${challenge.completed ? 'bg-[#4ADE80]/20 text-[#FF6B4A]' : 'bg-[#F4F2EE] text-[#8C7A76]'}`}>
      +{challenge.xp} XP
    </span>
  </div>
);

const RarePlantBadge = ({ rare }) => (
  <div className="flex items-center gap-2 p-2 bg-white rounded-xl shadow-sm" title={rare.unlock}>
    <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: `${rare.color}20` }}>
      <Star className="w-4 h-4" style={{ color: rare.color }} />
    </div>
    <div>
      <p className="text-xs font-semibold text-[#1A1514]">{rare.name}</p>
      <p className="text-[10px] text-[#8C7A76]">{rare.unlock}</p>
    </div>
  </div>
);

const GlowGarden = () => {
  const [garden, setGarden] = useState(null);
  const [planting, setPlanting] = useState(false);

  const fetchGarden = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/garden`, { withCredentials: true });
      setGarden(res.data);
      if (res.data.new_rare_unlocks?.length > 0) {
        res.data.new_rare_unlocks.forEach((r) => {
          toast.success(`Rare plant unlocked: ${r.name}!`);
        });
      }
    } catch { /* not logged in or error */ }
  }, []);

  useEffect(() => {
    fetchGarden();
  }, [fetchGarden]);

  const handlePlant = async () => {
    setPlanting(true);
    try {
      const res = await axios.post(`${API}/garden/plant`, {}, { withCredentials: true });
      if (res.data.status === 'already_planted') {
        toast.info('You already planted today! Come back tomorrow.');
      } else {
        const mult = res.data.streak_multiplier > 1 ? ` (${res.data.streak_multiplier}x streak bonus!)` : '';
        toast.success(`Planted ${res.data.plant.name}! +${res.data.xp_earned} XP${mult}`);
      }
      await fetchGarden();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Log today first to plant');
    } finally {
      setPlanting(false);
    }
  };

  if (!garden) return null;

  const allRares = RARE_PLANTS_DISPLAY.map((r) => {
    const unlocked = garden.rare_plants?.find((ur) => ur.id === r.id);
    return { ...r, unlocked: !!unlocked };
  });

  return (
    <div className="space-y-6" data-testid="glow-garden">
      {/* Garden Header */}
      <Card className="card-elevated border-0 overflow-hidden">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-[#1A1514]">
              <Flower2 className="w-5 h-5 text-[#4ADE80]" /> Your Glow Garden
            </CardTitle>
            <span className="text-xs text-[#8C7A76]">{garden.total_plants} plants grown</span>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <GardenGrid plants={garden.plants} />
          <LevelProgress level={garden.level} totalPlants={garden.total_plants} totalXp={garden.total_xp} />
          {!garden.has_today_log ? (
            <p className="text-center text-sm text-[#8C7A76] py-2">Log today to grow a new plant!</p>
          ) : (
            <Button onClick={handlePlant} disabled={planting} className="w-full bg-[#FF6B4A] hover:bg-[#E8552F] rounded-xl" data-testid="plant-seed-btn">
              {planting ? 'Planting...' : 'Plant Today\'s Seed'}
              <Sprout className="w-4 h-4 ml-2" />
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Daily Challenges */}
      <Card className="card-elevated border-0">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-[#1A1514]">
            <Target className="w-5 h-5 text-[#A388EE]" /> Daily Challenges
          </CardTitle>
          <p className="text-xs text-[#8C7A76]">Complete challenges to earn bonus XP</p>
        </CardHeader>
        <CardContent className="space-y-2">
          {garden.today_challenges?.map((ch) => (
            <ChallengeCard key={ch.id} challenge={ch} />
          ))}
        </CardContent>
      </Card>

      {/* Rare Plants Collection */}
      <Card className="card-elevated border-0">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-[#1A1514]">
            <Trophy className="w-5 h-5 text-[#F59E0B]" /> Rare Plant Collection
          </CardTitle>
          <p className="text-xs text-[#8C7A76]">{garden.rare_plants?.length || 0}/5 collected</p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {allRares.map((rare) => (
              rare.unlocked ? (
                <RarePlantBadge key={rare.id} rare={rare} />
              ) : (
                <div key={rare.id} className="flex items-center gap-2 p-2 bg-[#FFF2EC] rounded-xl opacity-50">
                  <div className="w-8 h-8 rounded-full bg-[#E5D0C9] flex items-center justify-center">
                    <Lock className="w-4 h-4 text-[#8C7A76]" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-[#8C7A76]">???</p>
                    <p className="text-[10px] text-[#8C7A76]">{rare.unlock}</p>
                  </div>
                </div>
              )
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const RARE_PLANTS_DISPLAY = [
  { id: "sun_orchid", name: "Sun Orchid", color: "#F97316", unlock: "14-day streak" },
  { id: "crystal_rose", name: "Crystal Rose", color: "#EC4899", unlock: "30-day streak" },
  { id: "jade_tree", name: "Jade Tree", color: "#10B981", unlock: "Detox score 95+" },
  { id: "phoenix_flower", name: "Phoenix Flower", color: "#EF4444", unlock: "Comeback after wilt" },
  { id: "starbloom", name: "Starbloom", color: "#6366F1", unlock: "7 challenges completed" },
];

export default GlowGarden;
